import {
  FC,
  useMemo,
  useState,
  MouseEvent,
  Fragment,
  useContext,
  useEffect,
} from 'react';
import {
  Button,
  Typography,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  MenuList,
  Divider,
} from '@mui/material';
import { useGetAllAssociatesForTransfer } from '@vs-online-only-app/state';
import { OnlineStatusIndicator } from '../../incoming-conversation/NewConversationRequestCard';
import FilterButtonIcon from './FilterButtonIcon';
import { TransferContext } from './TransferContext';

const AssociateFilters: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { associateFilters, setSelectedDepartmentIds, selectedDepartmentIds } =
    useContext(TransferContext);
  const { data: allAssociates } = useGetAllAssociatesForTransfer();

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const applyFilters = () => {
    closeMenu();
  };

  const clearFilters = () => {
    setSelectedDepartmentIds([]);
    closeMenu();
  };

  const departmentFilterOptions = useMemo(() => {
    const departments = associateFilters
      ?.find((filter) => filter.type === 'department')
      ?.tags?.filter((tag) => tag.type === 'department');

    //calculate how many online associates we have for each department
    const onlineAssociatesCountByDepartment: Record<string, number> =
      departments?.reduce((acc, department) => {
        const count =
          allAssociates?.filter((associate) => {
            const associateDepartmentIds = associate.departments.map(
              (d) => d._id,
            );
            return associateDepartmentIds.includes(department.id);
          }).length || 0;
        return { ...acc, [department.id]: count };
      }, {}) || {};

    const onlineDepartments = departments?.filter(
      (department) => onlineAssociatesCountByDepartment[department.id] > 0,
    );
    const offlineDepartments = departments?.filter(
      (department) => !onlineAssociatesCountByDepartment[department.id],
    );

    const onlineFilterOptions = onlineDepartments?.map((department) => ({
      id: department.id,
      label: `${department.name} (${
        onlineAssociatesCountByDepartment[department.id]
      })`,
    }));

    const offlineFilterOptions = offlineDepartments?.map((department) => ({
      id: department.id,
      label: department.name,
      disabled: true,
    }));

    return {
      online: onlineFilterOptions,
      offline: offlineFilterOptions,
    };
  }, [allAssociates, associateFilters]);

  // Set selected selectedDepartmentIds when filters are loaded
  useEffect(() => {
    const onlineDepartmentIds = departmentFilterOptions?.online?.map(
      ({ id }) => id,
    );
    if (!onlineDepartmentIds) return;
    setSelectedDepartmentIds(onlineDepartmentIds);
  }, [departmentFilterOptions?.online, setSelectedDepartmentIds]);

  const selectDepartment = (departmentId: string) => {
    setSelectedDepartmentIds((selectedDepartmentIds) => {
      if (selectedDepartmentIds?.includes(departmentId)) {
        return selectedDepartmentIds.filter((id) => id !== departmentId);
      }
      return [...(selectedDepartmentIds || []), departmentId];
    });
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <Button
        size="small"
        onClick={isMenuOpen ? closeMenu : openMenu}
        endIcon={
          <FilterButtonIcon
            isMenuOpen={isMenuOpen}
            length={selectedDepartmentIds?.length || 0}
          />
        }
        variant="outlined"
        sx={{ ...(!isMenuOpen && { borderColor: 'transparent' }) }}
      >
        Team
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { minWidth: '350px' } } }}
      >
        <MenuList>
          {departmentFilterOptions?.online?.length ? (
            <Stack>
              <Stack alignItems="center" direction="row" p={2} gap={0.5}>
                <OnlineStatusIndicator $isOnline />
                <Typography variant="body2" color="textSecondary">
                  Online
                </Typography>
              </Stack>
              {departmentFilterOptions.online.map(({ label, id }, index) => (
                <Fragment key={id}>
                  {index > 0 && <Divider variant="middle" />}
                  <MenuItem key={id} onClick={() => selectDepartment(id)}>
                    <ListItemText>{label}</ListItemText>
                    <Checkbox
                      checked={selectedDepartmentIds?.includes(id)}
                      size="small"
                    />
                  </MenuItem>
                </Fragment>
              ))}
            </Stack>
          ) : null}
          {departmentFilterOptions?.online?.length &&
          departmentFilterOptions?.offline?.length ? (
            <Divider />
          ) : null}
          {departmentFilterOptions?.offline?.length ? (
            <Stack>
              <Stack alignItems="center" direction="row" p={2} gap={0.5}>
                <OnlineStatusIndicator />
                <Typography variant="body2" color="textSecondary">
                  Offline
                </Typography>
              </Stack>
              {departmentFilterOptions.offline.map(
                ({ label, id, disabled }, index) => (
                  <Fragment key={id}>
                    {index > 0 && <Divider variant="middle" />}
                    <MenuItem disabled={disabled}>
                      <Typography>{label}</Typography>
                      <Checkbox
                        checked={selectedDepartmentIds?.includes(id)}
                        size="small"
                        disabled={disabled}
                      />
                    </MenuItem>
                  </Fragment>
                ),
              )}
            </Stack>
          ) : null}
        </MenuList>
        {departmentFilterOptions.online?.length ? (
          <Stack direction="row" gap={1} padding={2}>
            <Button
              size="small"
              onClick={clearFilters}
              variant="outlined"
              fullWidth
            >
              Clear filters
            </Button>
            <Button
              size="small"
              onClick={applyFilters}
              variant="contained"
              fullWidth
            >
              Apply filters
            </Button>
          </Stack>
        ) : null}
      </Menu>
    </>
  );
};

export default AssociateFilters;
